import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

// One shared AR-display component used by both the main Customers page
// (Billing tab) and the CRM call-logging page. All styling is inline so
// it drops in consistently next to either MUI or plain-CSS layouts.

export interface BillingInvoice {
  id: number;
  invoice_number: string;
  invoice_date: string | null;
  due_date: string | null;
  total_amount: string;
  balance_due: string;
  status: string;
  is_overdue: boolean;
  order_number?: string | null;
}

export interface BillingPayment {
  id: number;
  amount: string;
  payment_date: string | null;
  payment_method: string;
  invoice_id?: number | null;
  invoice_number?: string | null;
  transaction_id?: string | null;
}

export interface BillingArSummary {
  total_outstanding: string;
  overdue_count: number;
  overdue_amount: string;
}

interface Props {
  invoices: BillingInvoice[];
  payments: BillingPayment[];
  arSummary?: BillingArSummary;
  initialVisible?: number;
  /**
   * Where InvoiceDetail's "Back to Customer" button should land. Each
   * parent (Customers.tsx, CustomerCRM.tsx) passes its own value so the
   * panel doesn't have to know which page is hosting it. Forwarded into
   * the navigate URL as ?from=customer&returnTo=<path> and into
   * location.state as a fallback for hard reloads.
   */
  returnPath?: string;
}

const statusStyle = (status: string, isOverdue: boolean): React.CSSProperties => {
  if (isOverdue) return { background: '#fee2e2', color: '#991b1b' };
  switch ((status || '').toLowerCase()) {
    case 'paid': return { background: '#dcfce7', color: '#166534' };
    case 'partial': return { background: '#fef3c7', color: '#92400e' };
    case 'ach_pending': return { background: '#ede9fe', color: '#5b21b6' };
    case 'sent': return { background: '#dbeafe', color: '#1e40af' };
    case 'approved':
    case 'pending_review': return { background: '#e0f2fe', color: '#075985' };
    case 'void': return { background: '#e5e7eb', color: '#374151' };
    case 'draft': return { background: '#f3f4f6', color: '#4b5563' };
    default: return { background: '#f3f4f6', color: '#374151' };
  }
};

const methodStyle = (method: string): React.CSSProperties => {
  const m = (method || '').toLowerCase();
  if (m.includes('ach') || m.includes('bank')) return { background: '#dcfce7', color: '#166534' };
  if (m.includes('card') || m.includes('credit') || m.includes('visa') || m.includes('master')) return { background: '#dbeafe', color: '#1e40af' };
  if (m.includes('check')) return { background: '#e5e7eb', color: '#374151' };
  if (m.includes('cash')) return { background: '#fef9c3', color: '#854d0e' };
  return { background: '#f3f4f6', color: '#374151' };
};

const fmtMoney = (raw: string | number | null | undefined): string => {
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw ?? '0'));
  if (!isFinite(n)) return '$0.00';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const fmtDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const Badge: React.FC<{ children: React.ReactNode; style?: React.CSSProperties }> = ({ children, style }) => (
  <span
    style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 10,
      fontSize: 11,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      ...style,
    }}
  >
    {children}
  </span>
);

const Section: React.FC<{ title: string; count: number; children: React.ReactNode }> = ({ title, count, children }) => (
  <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
      <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{title}</div>
      <div style={{ fontSize: 12, color: '#6b7280' }}>{count} total</div>
    </div>
    {children}
  </div>
);

const CustomerBillingPanel: React.FC<Props> = ({ invoices, payments, arSummary, initialVisible = 5, returnPath }) => {
  const navigate = useNavigate();
  const [showAllInvoices, setShowAllInvoices] = useState(false);
  const [showAllPayments, setShowAllPayments] = useState(false);
  const [hoverInvoiceId, setHoverInvoiceId] = useState<number | null>(null);
  const [hoverPaymentId, setHoverPaymentId] = useState<number | null>(null);

  const outstanding = parseFloat(arSummary?.total_outstanding || '0');
  const overdueCount = arSummary?.overdue_count || 0;
  const overdueAmt = parseFloat(arSummary?.overdue_amount || '0');

  const visibleInvoices = showAllInvoices ? invoices : invoices.slice(0, initialVisible);
  const visiblePayments = showAllPayments ? payments : payments.slice(0, initialVisible);

  // Build the InvoiceDetail URL with both URL params (canonical, survives
  // refresh) and state (fallback used when present, ignored otherwise).
  const openInvoice = (invoiceId: number) => {
    const qs = new URLSearchParams();
    qs.set('from', 'customer');
    if (returnPath) qs.set('returnTo', returnPath);
    navigate(`/sales/invoices/${invoiceId}?${qs.toString()}`, {
      state: returnPath ? { fromCustomerReturnPath: returnPath } : undefined,
    });
  };

  return (
    <div>
      {outstanding > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
            padding: '10px 14px',
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <Badge style={{ background: '#f59e0b', color: '#fff' }}>
            Outstanding: {fmtMoney(outstanding)}
          </Badge>
          {overdueCount > 0 && (
            <Badge style={{ background: '#dc2626', color: '#fff' }}>
              {overdueCount} invoice{overdueCount === 1 ? '' : 's'} overdue — {fmtMoney(overdueAmt)}
            </Badge>
          )}
        </div>
      )}

      <Section title="Invoices" count={invoices.length}>
        {invoices.length === 0 ? (
          <div style={{ color: '#6b7280', textAlign: 'center', padding: 16 }}>No invoices on file</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visibleInvoices.map((inv) => {
                const isHover = hoverInvoiceId === inv.id;
                const baseBg = inv.is_overdue ? '#fef2f2' : '#fafafa';
                const hoverBg = inv.is_overdue ? '#fde8e8' : '#f1f5f9';
                return (
                <div
                  key={inv.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => openInvoice(inv.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openInvoice(inv.id);
                    }
                  }}
                  onMouseEnter={() => setHoverInvoiceId(inv.id)}
                  onMouseLeave={() => setHoverInvoiceId(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(110px, 1fr) minmax(90px, auto) minmax(110px, auto) minmax(110px, auto) minmax(90px, auto) minmax(100px, auto) minmax(100px, auto)',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: isHover ? hoverBg : baseBg,
                    borderLeft: inv.is_overdue ? '3px solid #dc2626' : '3px solid transparent',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: 'pointer',
                    transition: 'background-color 0.1s',
                  }}
                >
                  <span style={{ fontWeight: 700, color: '#111827' }}>{inv.invoice_number}</span>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>
                    {inv.order_number ? `#${inv.order_number}` : '—'}
                  </span>
                  <span style={{ color: '#6b7280' }}>{fmtDate(inv.invoice_date)}</span>
                  <span style={{ color: inv.is_overdue ? '#991b1b' : '#6b7280' }}>
                    Due {fmtDate(inv.due_date)}
                  </span>
                  <Badge style={statusStyle(inv.status, inv.is_overdue)}>
                    {inv.is_overdue ? 'Overdue' : inv.status}
                  </Badge>
                  <span style={{ color: '#374151', textAlign: 'right' }}>{fmtMoney(inv.total_amount)}</span>
                  <span
                    style={{
                      color: parseFloat(inv.balance_due) > 0 ? '#b91c1c' : '#16a34a',
                      fontWeight: 700,
                      textAlign: 'right',
                    }}
                  >
                    {fmtMoney(inv.balance_due)}
                  </span>
                </div>
                );
              })}
            </div>
            {invoices.length > initialVisible && (
              <button
                type="button"
                onClick={() => setShowAllInvoices((s) => !s)}
                style={{
                  marginTop: 10,
                  background: 'none',
                  border: 'none',
                  color: '#1e5e1e',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {showAllInvoices ? 'Show less' : `Show all ${invoices.length}`}
              </button>
            )}
          </>
        )}
      </Section>

      <Section title="Payment History" count={payments.length}>
        {payments.length === 0 ? (
          <div style={{ color: '#6b7280', textAlign: 'center', padding: 16 }}>No payments recorded</div>
        ) : (
          <>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {visiblePayments.map((p) => {
                const clickable = !!p.invoice_id;
                const isHover = hoverPaymentId === p.id;
                return (
                <div
                  key={p.id}
                  role={clickable ? 'button' : undefined}
                  tabIndex={clickable ? 0 : undefined}
                  onClick={clickable ? () => openInvoice(p.invoice_id!) : undefined}
                  onKeyDown={clickable ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      openInvoice(p.invoice_id!);
                    }
                  } : undefined}
                  onMouseEnter={() => clickable && setHoverPaymentId(p.id)}
                  onMouseLeave={() => clickable && setHoverPaymentId(null)}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'minmax(110px, auto) minmax(90px, auto) minmax(110px, auto) minmax(120px, auto) 1fr',
                    gap: 10,
                    alignItems: 'center',
                    padding: '10px 12px',
                    background: isHover ? '#f1f5f9' : '#fafafa',
                    border: '1px solid #e5e7eb',
                    borderRadius: 6,
                    fontSize: 13,
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'background-color 0.1s',
                  }}
                >
                  <span style={{ color: '#6b7280' }}>{fmtDate(p.payment_date)}</span>
                  <span style={{ fontWeight: 700, color: '#111827' }}>{fmtMoney(p.amount)}</span>
                  <Badge style={methodStyle(p.payment_method)}>{p.payment_method || 'Unknown'}</Badge>
                  <span style={{ color: '#374151' }}>
                    {p.invoice_number ? `Invoice ${p.invoice_number}` : '—'}
                  </span>
                  <span style={{ color: '#9ca3af', fontSize: 11, textAlign: 'right', fontFamily: 'monospace' }}>
                    {p.transaction_id || ''}
                  </span>
                </div>
                );
              })}
            </div>
            {payments.length > initialVisible && (
              <button
                type="button"
                onClick={() => setShowAllPayments((s) => !s)}
                style={{
                  marginTop: 10,
                  background: 'none',
                  border: 'none',
                  color: '#1e5e1e',
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: 600,
                }}
              >
                {showAllPayments ? 'Show less' : `Show all ${payments.length}`}
              </button>
            )}
          </>
        )}
      </Section>
    </div>
  );
};

export default CustomerBillingPanel;
